import { useState, useEffect } from 'react';
import { BRAND } from '@/brand';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginInput } from '@courier/shared';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, ShieldCheck, Building2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { API_BASE_URL } from '@/api/client';

interface LoginPageProps {
  portal: 'admin' | 'merchant';
  mode?: 'login' | 'signup';
}

export function LoginPage({ portal, mode = 'login' }: LoginPageProps) {
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => {
    document.body.classList.add('login-page');
    return () => document.body.classList.remove('login-page');
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const { loginAdmin, loginMerchant } = useAuth();

  const { register, handleSubmit, formState: { errors } } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginInput) => {
    setSubmitting(true);
    try {
      if (portal === 'admin') {
        await loginAdmin(data.email, data.password);
      } else {
        await loginMerchant(data.email, data.password);
      }
      toast.success('Welcome back!');
      const dest = portal === 'merchant' ? '/wallet' : '/dashboard';
      setTimeout(() => { window.location.href = dest; }, 200);
    } catch (e: any) {
      const msg = e?.response?.data?.message ?? e?.message ?? 'Login failed';
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const brand = portal === 'admin'
    ? { title: 'Admin Portal', subtitle: 'Super Admin + Internal Users', icon: <ShieldCheck size={28} /> }
    : { title: 'Merchant Portal', subtitle: 'Manage your shipments', icon: <Building2 size={28} /> };

  const apkEdition = portal === 'merchant' ? 'merchant' : 'internal';
  const apkLabel = portal === 'merchant' ? 'Merchant edition' : 'Internal team edition';

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary to-primary-800 text-white p-12 flex-col justify-between">
        {/* Wordmark + tagline as one lockup — a tagline belongs next to the name it qualifies,
            not floating on its own elsewhere on the panel. */}
        <div>
          <div className="font-bold text-2xl tracking-tight">{BRAND.name}</div>
          <div className="text-primary-200 text-sm mt-1 tracking-wide uppercase">{BRAND.tagline}</div>
        </div>
        <div>
          <h2 className="text-4xl font-bold leading-tight">{BRAND.headline}</h2>
          <p className="text-primary-200 mt-4 max-w-md">{BRAND.blurb}</p>
        </div>
        <div className="text-primary-300 text-sm">© {new Date().getFullYear()} {BRAND.copyright}</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-primary text-white p-3 rounded-xl">{brand.icon}</div>
            <div>
              <div className="font-bold text-2xl text-slate-900">{brand.title}</div>
              <div className="text-slate-500 text-sm">{brand.subtitle}</div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="text-xl font-semibold text-slate-900">
              {mode === 'signup' ? 'Create your account' : 'Welcome back'}
            </h3>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {portal === 'admin' ? 'Email or EMP Code' : 'Email'}
              </label>
              <input type="text" autoComplete="username" {...register('email')}
                className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                placeholder={portal === 'admin' ? 'you@company.com  OR  SHR001' : 'you@company.com'} />
              {portal === 'admin' && (
                <p className="text-xs text-slate-400 mt-1">Internal users can sign in with their employee code instead of email.</p>
              )}
              {errors.email && <p className="text-danger text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} autoComplete="current-password" {...register('password')}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none pr-10"
                  placeholder="********" />
                <button type="button" onClick={() => setShowPassword((v) => !v)}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-danger text-xs mt-1">{errors.password.message}</p>}
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-slate-600">
                <input type="checkbox" {...register('rememberMe')} className="rounded" />
                Remember me
              </label>
              <a href="/forgot-password" className="text-primary hover:underline">Forgot password?</a>
            </div>

            <button type="submit" disabled={submitting}
              className="w-full bg-primary text-white py-2.5 rounded-lg font-medium hover:bg-primary-700 transition disabled:opacity-60 flex items-center justify-center gap-2">
              {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
              {mode === 'signup' ? 'Create account' : 'Sign in'}
            </button>

            {portal === 'merchant' && (
              <p className="text-center text-sm text-slate-600 pt-2 border-t border-slate-100">
                New here?{' '}
                <a href={mode === 'login' ? '/signup' : '/login'} className="text-primary hover:underline font-medium">
                  {mode === 'login' ? 'Create an account' : 'Sign in'}
                </a>
              </p>
            )}

            <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <a href={`${API_BASE_URL}/download/apk/${apkEdition}`} target="_blank" rel="noreferrer"
                className="flex items-center justify-center gap-2 text-emerald-700 font-medium text-sm hover:underline">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a1 1 0 011 1v10.586l3.293-3.293a1 1 0 011.414 1.414l-5 5a1 1 0 01-1.414 0l-5-5a1 1 0 011.414-1.414L11 13.586V3a1 1 0 011-1zM4 18a1 1 0 011 1v2h14v-2a1 1 0 112 0v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3a1 1 0 011-1z"/></svg>
                Download Android App
              </a>
              <p className="text-xs text-emerald-600 text-center mt-1">{apkLabel}</p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
